/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { issueAppEntryPoint } from '@jira/issue__issue-view/src/issueAppEntryPoint.entrypoint.tsx';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { NestedEntryPoints } from '@jira/spa-apps__issue-navigator-issue-navigator-common/src/useJqlBuilderProps.tsx';
import { nativeIssueTableEntryPoint } from '@jira/issue-table__issue-table/src/ui/nativeIssueTableEntryPoint.entrypoint.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { jqlBuilderWithAiEntryPoint } from '@jira/jql-builder__with-ai/src/ui/jqlBuilderWithAiEntryPoint.entrypoint.tsx';
import type { GlobalIssueNavigatorEntryPointRuntimeProps } from './GlobalIssueNavigator.entrypoint.types.tsx';

type GlobalIssueNavigatorEntryPointRoot = EntryPointComponent<
	{},
	NestedEntryPoints,
	GlobalIssueNavigatorEntryPointRuntimeProps,
	{
		scrollElementHeightCalibration?: {
			scrollElementHeight: number;
			viewportHeightOverhead: number;
		};
	}
>;

// Matches only plain objects: arrays, `null`, `Date`, `RegExp`, functions, and
// primitives are all excluded, so the `Record<string, unknown>` narrowing
// reflects the exact set of runtime values that reach the property access below.
const isRecord = (value: unknown): value is Record<string, unknown> =>
	Object.prototype.toString.call(value) === '[object Object]';

/**
 * Safely resolves the scroll element height calibration for a route from an
 * untrusted experiment value. Returns `undefined` (so the prop is omitted and
 * the Issue Table uses its built-in fallback) unless the value is a plain object
 * keyed by route name whose entry has finite numeric `scrollElementHeight` and
 * `viewportHeightOverhead`.
 */
const getScrollElementHeightCalibration = (
	calibration: unknown,
	routeName: string,
): { scrollElementHeight: number; viewportHeightOverhead: number } | undefined => {
	if (!isRecord(calibration)) {
		return undefined;
	}

	const entry = calibration[routeName];
	if (!isRecord(entry)) {
		return undefined;
	}

	const { scrollElementHeight, viewportHeightOverhead } = entry;
	if (
		typeof scrollElementHeight !== 'number' ||
		!Number.isFinite(scrollElementHeight) ||
		typeof viewportHeightOverhead !== 'number' ||
		!Number.isFinite(viewportHeightOverhead)
	) {
		return undefined;
	}

	return { scrollElementHeight, viewportHeightOverhead };
};

export const globalIssueNavigatorEntryPoint: EntryPoint<
	GlobalIssueNavigatorEntryPointRoot,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<GlobalIssueNavigatorEntryPointRoot>(
		/* webpackChunkName: "async-global-issue-navigator-entrypoint" */ './GlobalIssueNavigatorSPAMemo.tsx',
		{ entryFsSsrLiftImportToModule: true },
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		let issueKey = entryPointParams?.context?.match?.params?.issueKey;

		// current prod bug workaround: issueKey is always undefined since issueKey is passed in as selectedIssue from query param
		if (fg('nin_issue_app_conditional_entrypoint')) {
			issueKey = entryPointParams?.context?.query?.selectedIssue;
		}

		let preloadProps = {};
		// exclude nativeIssueTableEntryPoint and jqlBuilderWithAiEntryPoint from preloading on the issue route unless glance panel is enabled
		if (!issueKey || expVal('nin_glance_panel', 'isEnabled', false)) {
			preloadProps = {
				entryPoints: {
					nativeIssueTableEntryPoint: {
						entryPoint: nativeIssueTableEntryPoint,
						entryPointParams,
					},
					...(fg('jql_builder_basic_ssr_stability_fixes')
						? {
								jqlBuilderWithAiEntryPoint: {
									entryPoint: jqlBuilderWithAiEntryPoint,
									entryPointParams,
								},
							}
						: {}),
				},
			};
		} else if (
			issueKey &&
			/* eslint-disable jira/ff/no-work-item-modernization-exp-val -- Existing WIM rollout check; migrate to isWorkItemModernizationGatesEnabled. */
			!(
				expVal('work_item_modernization', 'isEnabled', false) ||
				fg('enable_work_item_for_quadratic_release')
			) &&
			/* eslint-enable jira/ff/no-work-item-modernization-exp-val */
			fg('nin_issue_app_conditional_entrypoint')
		) {
			preloadProps = {
				entryPoints: {
					issueAppEntryPoint: {
						entryPoint: issueAppEntryPoint,
						entryPointParams,
					},
				},
			};
		}

		// Per-route scroll element height calibration is delivered via the
		// `issue_table_virtualizer_dynamic_estimators` experiment's `calibration`
		// parameter, a `Record<routeName, ScrollElementHeightCalibration>`. The
		// experiment value is untrusted (it comes from remote config), so it is read
		// loosely and validated before use: the route's entry must be an object with
		// numeric `scrollElementHeight` and `viewportHeightOverhead`. When the route
		// has no valid entry the prop is omitted and the Issue Table falls back to its
		// built-in default.
		const scrollElementHeightCalibration = expVal(
			'issue_table_virtualizer_dynamic_estimators',
			'isEnabled',
			false,
		)
			? getScrollElementHeightCalibration(
					expVal<unknown>('issue_table_virtualizer_dynamic_estimators', 'calibration', null),
					entryPointParams.context.route.name,
				)
			: undefined;

		return {
			...preloadProps,
			...(scrollElementHeightCalibration ? { extraProps: { scrollElementHeightCalibration } } : {}),
		};
	},
});
